// "By Character" view of the producer script book. Cues are grouped under
// the character row that owns them. Identical row component as the by-scene
// view; only the grouping changes.

import type { FrameRate } from '@ohear/shared'
import { copy } from '@ohear/shared'
import { Card } from '@ohear/ui'
import type { CharacterRow, CueRow } from '../../lib/api/producer-types'
import { useT } from '../../lib/i18n/use-t'
import { CueRowDisplay } from './CueRowDisplay'
import type { ScriptBookByScenePropsRow } from './ScriptBookByScene'

export interface ScriptBookByCharacterProps {
  characters: CharacterRow[]
  cues: CueRow[]
  frameRate: FrameRate
  rowState: Map<string, ScriptBookByScenePropsRow>
  characterColors: Map<string, string>
  onToggleSelect: (cueId: string, next: boolean) => void
  onStartEdit: (cueId: string) => void
  onCancelEdit: () => void
  onSave: (cueId: string, patch: Partial<CueRow>) => void
  onSplit: (cueId: string) => void
  onMerge: (cueId: string) => void
}

export function ScriptBookByCharacter({
  characters,
  cues,
  frameRate,
  rowState,
  characterColors,
  onToggleSelect,
  onStartEdit,
  onCancelEdit,
  onSave,
  onSplit,
  onMerge,
}: ScriptBookByCharacterProps) {
  const t = useT()
  const cuesByCharId = groupByCharacterId(cues)
  const unassigned = cues.filter((c) => !c.character_id)

  return (
    <div className="space-y-4">
      {characters.map((character) => {
        const charCues = cuesByCharId.get(character.id) ?? []
        return (
          <Card
            key={character.id}
            title={
              <div className="flex items-center gap-3">
                <span
                  className="inline-block h-3 w-3 rounded-full"
                  style={{ background: characterColors.get(character.id) ?? '#888' }}
                  aria-hidden="true"
                />
                <span className="text-[14px] font-medium">{character.display_name}</span>
                <span className="text-[12px] font-mono text-[var(--text-2)]">
                  {character.character_code}
                </span>
              </div>
            }
            actions={
              <span className="text-[12px] text-[var(--text-2)]">
                {character.line_count} line{character.line_count === 1 ? '' : 's'}
              </span>
            }
          >
            {charCues.length === 0 ? (
              <p className="m-0 text-[13px] text-[var(--text-2)]">
                {t(copy.common.EMPTY.no_cues_yet)}
              </p>
            ) : (
              <CueTable
                cues={charCues}
                frameRate={frameRate}
                rowState={rowState}
                characterColors={characterColors}
                onToggleSelect={onToggleSelect}
                onStartEdit={onStartEdit}
                onCancelEdit={onCancelEdit}
                onSave={onSave}
                onSplit={onSplit}
                onMerge={onMerge}
              />
            )}
          </Card>
        )
      })}
      {unassigned.length > 0 ? (
        <Card
          title="Unassigned cues"
          actions={<span className="text-[12px] text-[var(--amber)]">{unassigned.length}</span>}
        >
          <CueTable
            cues={unassigned}
            frameRate={frameRate}
            rowState={rowState}
            characterColors={characterColors}
            onToggleSelect={onToggleSelect}
            onStartEdit={onStartEdit}
            onCancelEdit={onCancelEdit}
            onSave={onSave}
            onSplit={onSplit}
            onMerge={onMerge}
          />
        </Card>
      ) : null}
    </div>
  )
}

function CueTable(props: {
  cues: CueRow[]
  frameRate: FrameRate
  rowState: Map<string, ScriptBookByScenePropsRow>
  characterColors: Map<string, string>
  onToggleSelect: (cueId: string, next: boolean) => void
  onStartEdit: (cueId: string) => void
  onCancelEdit: () => void
  onSave: (cueId: string, patch: Partial<CueRow>) => void
  onSplit: (cueId: string) => void
  onMerge: (cueId: string) => void
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[11px] uppercase text-[var(--text-3)]">
            <th className="w-6 px-2 py-1" />
            <th className="w-12 px-2 py-1">#</th>
            <th className="px-2 py-1">Char</th>
            <th className="px-2 py-1">In / Out</th>
            <th className="px-2 py-1">Line / Effort</th>
            <th className="px-2 py-1">Predict</th>
            <th className="px-2 py-1">Actions</th>
          </tr>
        </thead>
        <tbody>
          {props.cues.map((cue) => {
            const state = props.rowState.get(cue.id)
            const characterColor = cue.character_id
              ? props.characterColors.get(cue.character_id)
              : undefined
            return (
              <CueRowDisplay
                key={cue.id}
                cue={cue}
                frameRate={props.frameRate}
                selected={state?.selected ?? false}
                isEditing={state?.isEditing ?? false}
                characterColor={characterColor}
                onSelect={(n) => props.onToggleSelect(cue.id, n)}
                onStartEdit={() => props.onStartEdit(cue.id)}
                onCancelEdit={props.onCancelEdit}
                onSave={(patch) => props.onSave(cue.id, patch)}
                onSplit={() => props.onSplit(cue.id)}
                onMerge={() => props.onMerge(cue.id)}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function groupByCharacterId(cues: CueRow[]): Map<string, CueRow[]> {
  const map = new Map<string, CueRow[]>()
  for (const cue of cues) {
    if (!cue.character_id) continue
    const arr = map.get(cue.character_id)
    if (arr) arr.push(cue)
    else map.set(cue.character_id, [cue])
  }
  return map
}
